import React, { useEffect, useState, useCallback } from 'react';
import { Tag, Plus, Edit2, Trash2 } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import DataTable from '../../components/common/DataTable';
import EmptyState from '../../components/common/EmptyState';
import StatusBadge from '../../components/common/StatusBadge';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Select from '../../components/common/Select';
import SearchInput from '../../components/common/SearchInput';
import { campaignService } from '../../services/campaignService';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';
import { useToast } from '../../hooks/useToast';
import CampaignFormModal from './CampaignFormModal';

const TYPE_LABELS = {
  DISCOUNT_PERCENT: 'Desconto %',
  DISCOUNT_FIXED: 'Desconto fixo',
  FREE_SHIPPING: 'Frete grátis'
};

const CampaignsManagement = () => {
  const { success, error: toastError } = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: '', type: '', isActive: '' });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, campaign: null });
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await campaignService.list({
        q: filters.q || undefined,
        type: filters.type || undefined,
        isActive: filters.isActive === '' ? undefined : filters.isActive === 'true',
        page,
        limit: 20
      });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (payload) => {
    try {
      if (modal.campaign?.id) {
        await campaignService.update(modal.campaign.id, payload);
        success('Campanha atualizada.');
      } else {
        await campaignService.create(payload);
        success('Campanha criada.');
      }
      setModal({ open: false, campaign: null });
      load();
    } catch (err) {
      toastError(err?.response?.data?.message || 'Falha ao salvar campanha.');
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    try {
      await campaignService.remove(confirmRemove.id);
      success('Campanha desativada.');
      setConfirmRemove(null);
      load();
    } catch (err) {
      toastError(err?.response?.data?.message || 'Falha ao desativar.');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Nome',
      render: (c) => (
        <div>
          <p className="font-medium text-app-text">{c.name}</p>
          {c.code && (
            <p className="text-xs font-mono text-app-text-secondary">Código: {c.code}</p>
          )}
        </div>
      )
    },
    {
      key: 'type',
      header: 'Tipo',
      render: (c) => <span className="text-sm">{TYPE_LABELS[c.type] || c.type}</span>
    },
    {
      key: 'value',
      header: 'Valor',
      render: (c) => {
        if (c.type === 'FREE_SHIPPING') return <span className="text-app-text-secondary">—</span>;
        if (c.type === 'DISCOUNT_PERCENT') return `${Number(c.value).toFixed(0)}%`;
        return formatPrice(c.value);
      }
    },
    {
      key: 'uses',
      header: 'Usos',
      render: (c) => (
        <span className="text-sm">
          {c.currentUses}{c.maxUses != null ? ` / ${c.maxUses}` : ''}
        </span>
      )
    },
    {
      key: 'expiresAt',
      header: 'Expira',
      render: (c) => c.expiresAt ? (
        <span className="text-xs text-app-text-secondary">{formatDateTime(c.expiresAt)}</span>
      ) : (
        <span className="text-xs text-app-text-secondary">Sem prazo</span>
      )
    },
    {
      key: 'status',
      header: 'Status',
      render: (c) => <StatusBadge status={c.isActive ? 'active' : 'inactive'} />
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 110,
      render: (c) => (
        <div className="flex items-center gap-1 justify-end">
          <button
            type="button"
            className="p-1.5 rounded text-app-text-secondary hover:bg-app-surface-hover hover:text-primary"
            onClick={(e) => { e.stopPropagation(); setModal({ open: true, campaign: c }); }}
            aria-label="Editar"
            title="Editar"
          >
            <Edit2 size={16} />
          </button>
          <button
            type="button"
            className="p-1.5 rounded text-app-text-secondary hover:bg-error/10 hover:text-error"
            onClick={(e) => { e.stopPropagation(); setConfirmRemove(c); }}
            aria-label="Desativar"
            title="Desativar"
            disabled={!c.isActive}
          >
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Campanhas</h1>
          <p className="text-sm text-app-text-secondary">
            Cupons de desconto e frete grátis para a sua loja.
          </p>
        </div>
        <Button leftIcon={<Plus size={16} />} onClick={() => setModal({ open: true, campaign: null })}>
          Nova campanha
        </Button>
      </div>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <SearchInput
            value={filters.q}
            onChange={(q) => { setFilters((f) => ({ ...f, q })); setPage(1); }}
            placeholder="Buscar por nome ou código…"
            debounceMs={300}
          />
          <Select
            value={filters.type}
            onChange={(e) => { setFilters((f) => ({ ...f, type: e.target.value })); setPage(1); }}
            options={[
              { value: '', label: 'Todos tipos' },
              { value: 'DISCOUNT_PERCENT', label: 'Desconto %' },
              { value: 'DISCOUNT_FIXED', label: 'Desconto fixo' },
              { value: 'FREE_SHIPPING', label: 'Frete grátis' }
            ]}
          />
          <Select
            value={filters.isActive}
            onChange={(e) => { setFilters((f) => ({ ...f, isActive: e.target.value })); setPage(1); }}
            options={[
              { value: '', label: 'Ativas e inativas' },
              { value: 'true', label: 'Apenas ativas' },
              { value: 'false', label: 'Apenas inativas' }
            ]}
          />
        </div>
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
        onRowClick={(c) => setModal({ open: true, campaign: c })}
        empty={
          <EmptyState
            icon={Tag}
            title={filters.q || filters.type || filters.isActive ? 'Nenhuma campanha bate com os filtros.' : 'Nenhuma campanha ainda.'}
            description="Crie cupons de desconto ou frete grátis pra atrair mais pedidos."
            action={
              <Button leftIcon={<Plus size={16} />} onClick={() => setModal({ open: true, campaign: null })}>
                Criar campanha
              </Button>
            }
          />
        }
      />

      <CampaignFormModal
        isOpen={modal.open}
        onClose={() => setModal({ open: false, campaign: null })}
        onSubmit={handleSubmit}
        initial={modal.campaign}
      />

      <ConfirmDialog
        isOpen={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={handleRemove}
        title="Desativar campanha?"
        message={`A campanha "${confirmRemove?.name}" deixará de aceitar novos usos. O histórico de pedidos é preservado.`}
        confirmLabel="Desativar"
        variant="danger"
      />
    </div>
  );
};

export default CampaignsManagement;
